<script setup lang="ts">
import type { ScenarioChatForm_Message_BasicSelectionItem_Action } from '../../../types'
import MessageSelectionActionAssignPlannerAccount from './MessageSelectionActionAssignPlannerAccount.vue'
import { computed, reactive, ref } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import MenuContent from '@/components/molecules/Menu/MenuContent.vue'
import MenuItem from '@/components/molecules/Menu/MenuItem.vue'
import MenuPortal from '@/components/molecules/Menu/MenuPortal.vue'
import MenuRoot from '@/components/molecules/Menu/MenuRoot.vue'
import MenuTrigger from '@/components/molecules/Menu/MenuTrigger.vue'
import { useInternalScenarioChatFormStore } from '../../../form/form'

const props = defineProps<{
  actions: ScenarioChatForm_Message_BasicSelectionItem_Action[]
  selectedMessageClientOnlyId: string
  selectionIndex: number
}>()

const formStore = reactive(useInternalScenarioChatFormStore())

type ActionMenuItem = {
  type: string // あとで型で値を限定する
  label: string
  onSelect: (e: Event) => void
}

const actionMenuOpen = ref(false)

const allActionMenuItems = computed<ActionMenuItem[]>(() => {
  return [
    {
      type: 'AssignStaff',
      label: '担当者を割り当てる',
      onSelect: () => {
        formStore?.updateMessage(props.selectedMessageClientOnlyId, draft => {
          if (draft.selections.type !== 'BasicSelections') {
            throw new Error('draft.selections.type is not BasicSelections')
          }

          draft.selections.basicSelections[props.selectionIndex].actions.push({
            type: 'AssignStaff',
            staffGUID: formStore.staffs[0].guid,
          })
        })
      },
    },
    // {
    //   // 仮の値です
    //   type: 'RecordAnswer',
    //   label: '回答を記録する',
    //   onSelect: () => {
    //     window.alert('まだ実装されていません')
    //   },
    // },
  ]
})

const selectableActionMenuItems = computed<ActionMenuItem[]>(() => {
  return allActionMenuItems.value.filter(item => {
    return !props.actions.some(action => action.type === item.type)
  })
})

const handleActionDelete = (e: MouseEvent, actionIndex: number) => {
  e.preventDefault()

  const confirmed = window.confirm('アクションを削除します。よろしいですか？')

  if (confirmed) {
    formStore?.updateMessage(props.selectedMessageClientOnlyId, draft => {
      if (draft.selections.type !== 'BasicSelections') {
        throw new Error('draft.selections.type is not BasicSelections')
      }

      draft.selections.basicSelections[props.selectionIndex].actions.splice(
        actionIndex,
        1
      )
    })
  }
}

const canAddAction = computed(() => {
  return selectableActionMenuItems.value.length > 0
})
</script>

<template>
  <div>
    <div>
      <template v-for="(action, i) of props.actions" :key="action.type">
        <section v-if="action.type === 'AssignStaff'" :class="$style.action">
          <h1 :class="$style.actionHeader">担当者を割り当てる</h1>
          <MessageSelectionActionAssignPlannerAccount
            :selectedMessageClientOnlyId="props.selectedMessageClientOnlyId"
            :selectionIndex="props.selectionIndex"
            :actionIndex="i"
          />
          <div :class="$style.actionFooter">
            <button
              :class="$style.deleteButton"
              @click.prevent.stop="e => handleActionDelete(e, i)"
            >
              このアクションを削除する
            </button>
          </div>
        </section>
      </template>
    </div>
    <div v-if="canAddAction" :class="$style.addButtonWrapper">
      <MenuRoot v-model:open="actionMenuOpen">
        <MenuTrigger>
          <FlatButton variant="base" size="medium">
            アクションを追加する
          </FlatButton>
        </MenuTrigger>

        <MenuPortal>
          <MenuContent>
            <MenuItem
              v-for="item of selectableActionMenuItems"
              :key="item.type"
              :value="item.type"
              @select="item.onSelect"
            >
              {{ item.label }}
            </MenuItem>
          </MenuContent>
        </MenuPortal>
      </MenuRoot>
    </div>
  </div>
</template>

<style lang="scss" module>
.action {
  background-color: $color-black-100;
  border-radius: 4px;
  padding: 16px 12px;

  &:not(:first-child) {
    margin-top: 12px;
  }
}

.actionHeader {
  font-size: 15px;
  font-weight: 500;
}

.actionFooter {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.deleteButton {
  color: $color-text-and-object-low-emphasis;
  font-size: 14px;

  &:hover {
    color: $color-caution-accent;
    text-decoration: underline;
  }
}

.addButtonWrapper {
  margin-top: 8px;
}
</style>
